/**
 * Where the header's Back / Forward state comes from. Nothing here is stored
 * by the app; the browser or the router already owns the history.
 *
 * Preferred: the Navigation API (`window.navigation`, Baseline 2026).
 * `navigation.entries()` only lists same-origin contiguous entries, so
 * `canGoBack` already means "there is a previous in-app entry" and the header
 * never backs the user out of the app.
 *
 * Fallback: React Router stores the entry index in `history.state.idx`
 * (initialised to 0 on first load), which is enough for Back. Forward is not
 * derivable there and degrades to disabled.
 */
export type HistorySource = {
  canGoBack: boolean;
  canGoForward: boolean;
  /** `pathname + search` of the previous / next in-app entry when known. */
  prevPath: string | null;
  nextPath: string | null;
};

type NavigationEntryLike = { index: number; url: string | null };

/** The subset of the Navigation API we read; typed locally so older TS libs work. */
type NavigationLike = {
  canGoBack: boolean;
  canGoForward: boolean;
  currentEntry: NavigationEntryLike | null;
  entries(): NavigationEntryLike[];
};

const readNavigation = (): NavigationLike | null => {
  if (typeof window === "undefined") return null;
  const nav = (window as unknown as { navigation?: NavigationLike }).navigation;
  return nav && typeof nav.entries === "function" ? nav : null;
};

const pathOf = (entry: NavigationEntryLike | undefined): string | null => {
  if (!entry?.url) return null;
  try {
    const url = new URL(entry.url, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
};

const readRouterIndex = (): number => {
  if (typeof window === "undefined") return 0;
  const state = window.history.state as { idx?: unknown } | null;
  return typeof state?.idx === "number" ? state.idx : 0;
};

export function readHistorySource(): HistorySource {
  const nav = readNavigation();
  if (nav) {
    const entries = nav.entries();
    const index = nav.currentEntry?.index ?? -1;
    return {
      canGoBack: nav.canGoBack,
      canGoForward: nav.canGoForward,
      prevPath: index > 0 ? pathOf(entries[index - 1]) : null,
      nextPath: index >= 0 ? pathOf(entries[index + 1]) : null
    };
  }

  return {
    canGoBack: readRouterIndex() > 0,
    canGoForward: false,
    prevPath: null,
    nextPath: null
  };
}
